import { useMemo } from "react";
import { Chart } from "primereact/chart";
import {
  CHART_GRID_COLOR,
  CHART_PALETTE,
  CHART_TEXT_COLOR,
  baseLegend,
  baseTooltip,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
} from "./chartTheme";

export interface BarSeries {
  label: string;
  data: number[];
  // One color for the series, or one per bar
  color?: string | string[];
  stack?: string;
}

interface MultiBarChartProps {
  labels: string[];
  series: BarSeries[];
  horizontal?: boolean;
  stacked?: boolean;
  money?: boolean;
  height?: number;
  // Receives the category index of the clicked bar
  onBarClick?: (index: number) => void;
  showLegend?: boolean;
  // Max characters shown on category ticks (full label stays in tooltip)
  maxLabelLength?: number;
  // Overrides the money / number formatting of axis ticks and tooltips
  formatValue?: (v: number) => string;
}

/**
 * Generic grouped / stacked bar chart (vertical or horizontal).
 */
export default function MultiBarChart({
  labels,
  series,
  horizontal = false,
  stacked = false,
  money = true,
  height = 320,
  onBarClick,
  maxLabelLength = 22,
  showLegend = true,
  formatValue,
}: MultiBarChartProps) {
  const data = useMemo(
    () => ({
      labels,
      datasets: series.map((s, i) => ({
        label: s.label,
        data: s.data,
        stack: s.stack,
        backgroundColor: s.color ?? CHART_PALETTE[i % CHART_PALETTE.length],
        borderRadius: 4,
        maxBarThickness: 38,
      })),
    }),
    [labels, series],
  );

  const options = useMemo(() => {
    const valueFmt = formatValue ?? (money ? formatMoneyCompact : formatNumber);
    const tooltipFmt = formatValue ?? (money ? formatMoney : formatNumber);
    const valueAxis = {
      stacked,
      beginAtZero: true,
      ticks: {
        color: CHART_TEXT_COLOR,
        callback: (v: number | string) => valueFmt(Number(v)),
      },
      grid: { color: CHART_GRID_COLOR },
    };
    const categoryAxis = {
      stacked,
      ticks: {
        color: CHART_TEXT_COLOR,
        autoSkip: false,
        callback: function (this: any, v: number | string) {
          const label = String(this.getLabelForValue(Number(v)) ?? "");
          return label.length > maxLabelLength
            ? `${label.substring(0, maxLabelLength - 1)}…`
            : label;
        },
      },
      grid: { display: false },
    };

    return {
      indexAxis: horizontal ? "y" : "x",
      maintainAspectRatio: false,
      responsive: true,
      // "index" mode searches along x by default: horizontal bars must search
      // along y, or hover / click resolve to a different bar than the pointer
      interaction: {
        mode: "index",
        intersect: false,
        axis: horizontal ? "y" : "x",
      },
      plugins: {
        legend: { ...baseLegend, display: showLegend },
        tooltip: {
          ...baseTooltip,
          callbacks: {
            label: (ctx: any) =>
              ` ${ctx.dataset.label}: ${tooltipFmt(
                horizontal ? ctx.parsed.x : ctx.parsed.y,
              )}`,
          },
        },
      },
      scales: horizontal
        ? { x: valueAxis, y: categoryAxis }
        : { x: categoryAxis, y: valueAxis },
      onClick: onBarClick
        ? (_evt: unknown, elements: { index: number }[]) => {
            if (elements?.length) onBarClick(elements[0].index);
          }
        : undefined,
      onHover: onBarClick
        ? (evt: any, elements: unknown[]) => {
            const target = evt?.native?.target as HTMLElement | undefined;
            if (target) target.style.cursor = elements.length ? "pointer" : "default";
          }
        : undefined,
    };
  }, [
    horizontal,
    stacked,
    money,
    onBarClick,
    maxLabelLength,
    showLegend,
    formatValue,
  ]);

  return (
    <div style={{ position: "relative", height, width: "100%" }}>
      <Chart
        type="bar"
        data={data}
        options={options}
        style={{ height: "100%", width: "100%" }}
      />
    </div>
  );
}
